/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import GateLogCard
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/GateLogCard";
import LivePoultryTruckTransportSection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/LivePoultryTruck/LivePoultryTruckTransportSection";
import CustomDateTimePicker from "../../../../../../../components/CustomDateTimePicker";
import {CustomTextField} from "../../../../../../../components/CustomTextField";
import {GATE_LOG_NOTES_LENGTH} from "../../../EntryAndExitForms/Details/Model/Model";
import {IconDocumentVehicleGate} from "../../../../../../../components/buttons/IconButtons";
import {isNotEmpty} from "../../../../../../../Apis/functions";
import { PERIOD_TIME_UNIT_ENUM } from "../../../../../../../enums/PeriodTimeUnitEnum";

/**
 * Fields of the vehicle the guard reads at the gate.
 *
 * None of them is asked for from the requester, so the folio is the one that
 * says which of them belong to the gate: the value it already carries is only
 * read and the one it left blank is the one the guard completes.
 */
export const GUARD_VEHICLE_FIELDS = [
    {
        id: 'txt_gvm_company_or_area',
        name: 'companyOrArea',
        trans: 'company_or_area',
        placeholder: 'enter_the_company_or_internal_area',
        className: 'col-12'
    },
    {
        id: 'txt_gvm_plate_no',
        name: 'plateNo',
        trans: 'plate_no',
        placeholder: 'enter_plate_or_unit_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_gvm_vehicle_model',
        name: 'vehicleModel',
        trans: 'vehicle_model',
        placeholder: 'enter_vehicle_make_and_model',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_gvm_driver_s_name',
        name: 'driversName',
        trans: 'driver_s_name',
        placeholder: 'enter_driver_s_name',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_gvm_license_no',
        name: 'licenseNo',
        trans: 'license_no',
        placeholder: 'enter_license_number',
        className: 'col-sm-12 col-md-6'
    }
];

/**
 * Fields of the vehicle as the guard captures them at the gate.
 *
 * A value the requester registered travels with the folio, so it is only read;
 * the one it left blank is the one the guard completes, because the movement is
 * not closed without the unit complete. A log already written is read whole.
 *
 * @param {Object} item - Folio the movement belongs to.
 * @param {boolean} disabled - Whether the log was already written.
 *
 * @returns {Array} Fields the guard reads the vehicle with.
 */
export const getGuardVehicleFields = (item, disabled) =>
    GUARD_VEHICLE_FIELDS.map((field) => {
        const registered = isNotEmpty(item?.[field.name]);

        return {
            ...field,
            disabled: disabled || registered,
            required: disabled || !registered
        };
    });

/**
 * Gate log the guard completes when the vehicle is at the gate and the movement
 * only waits for its dispatch.
 *
 * What the requester registered travels with the folio and is only read, and
 * what it left blank is the one the guard completes, the same way every form of
 * material control is closed at the gate.
 *
 * @param {Function} t - Translation handler.
 * @param {Object} item - Folio the movement belongs to.
 * @param {Object} gateLog - Gate log captured by the guard.
 * @param {Function} handleChange - Change handler of the fields of the gate log.
 * @param {Object} directionOption - Direction the movement takes.
 * @param {boolean} emptyFields - Whether the required fields are marked.
 * @param {boolean} disabled - Whether the gate log is only read.
 * @param {boolean} showTransport - Whether the vehicle is read with the log,
 * because it is captured in the moment it is at the gate and a movement that
 * already read it there does not open it again.
 * @param {boolean} transportDisabled - Whether the vehicle is only read, because
 * it was already written on the step before this one.
 * @param {Object} contentRef - Reference of the content of the card.
 *
 * @returns {JSX.Element} Card with the vehicle and the moment of the movement.
 */
const GuardVehicleMovementGateLogSection = ({
                                                t,
                                                item,
                                                gateLog,
                                                handleChange,
                                                directionOption,
                                                emptyFields,
                                                disabled = false,
                                                showTransport = true,
                                                transportDisabled = false,
                                                contentRef
                                            }) => {

    const fields = getGuardVehicleFields(item, disabled || transportDisabled);
    // The date of the movement is optional for the requester, so the one it
    // declared is only read and the gate writes it only when it was left blank
    const isDateRegistered = isNotEmpty(item?.expectedMovementDate);
    const isDateDisabled = disabled || isDateRegistered;
    // The notes of the movement are optional the same way, so the ones the
    // requester wrote are only read and the gate writes them when left blank
    const isNotesRegistered = isNotEmpty(item?.movementNotes);
    const isNotesDisabled = disabled || isNotesRegistered;

    return (
        <GateLogCard t={t} Icon={IconDocumentVehicleGate} title={'gate_log'} contentRef={contentRef}>
            <div className={"px-3"}>
                {showTransport &&
                    <LivePoultryTruckTransportSection
                        t={t}
                        materialRequest={gateLog}
                        handleChange={handleChange}
                        emptyFields={emptyFields}
                        fields={fields}
                        contentClassName={'row input-padding-top pb-3'}
                        card={false}
                    />
                }

                {directionOption &&
                    <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
                        <div className={"d-flex align-items-center p-2"}>
                            <div className={`mr-2 ${directionOption.iconBackground} p-2 border-radius-8px d-flex align-items-center`}>
                                <directionOption.Icon className={directionOption.iconColor}/>
                            </div>
                            <p className={"fs-14px ibl-font-w-600 d-flex align-items-center"}>
                                {t(directionOption.trans)}
                            </p>
                        </div>
                        <div className={"border-bottom-shades-300 mb-3"}/>

                        <div className={"row p-3"}>
                            <div className={"col-12"}>
                                <CustomDateTimePicker
                                    id={`dp_gvm_${directionOption.dateName}`}
                                    name={directionOption.dateName}
                                    onChange={(e) => {
                                        handleChange({target: {value: e, name: directionOption.dateName}});
                                    }}
                                    trans={directionOption.dateTrans}
                                    value={gateLog?.[directionOption.dateName]}
                                    required={!isDateDisabled}
                                    disabled={isDateDisabled}
                                    openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                                    disablePast={true}
                                    emptyFields={emptyFields && !isDateDisabled}
                                />
                            </div>
                            <div className={"col-12"}>
                                <CustomTextField
                                    id={'txt_gvm_movement_notes'}
                                    length={GATE_LOG_NOTES_LENGTH}
                                    name={'movementNotes'}
                                    value={isNotesRegistered ? item?.movementNotes : gateLog?.movementNotes}
                                    trans={'notes'}
                                    type={'text'}
                                    multiline={true}
                                    rows={4}
                                    onChange={handleChange}
                                    disabled={isNotesDisabled}
                                    required={false}
                                    emptyFields={false}
                                    placeholder={t('add_any_relevant_notes_or_observations')}
                                />
                            </div>
                        </div>
                    </div>
                }
            </div>
        </GateLogCard>
    );
};

export default GuardVehicleMovementGateLogSection;
